export const ExpressionNodeQueryDescriptions = {
  key: "Visitor attribute or parameter name to match (e.g., 'utm_source', 'device_type').",
  value:
    "Expected value to compare against the visitor's actual value for this key. Constrained by `type`: when type is 'device' this must be exactly 'any', 'mobile', or 'desktop'.",
  filter: "Comparison operator (e.g., equals, contains, startsWith).",
  type: "Category of the parameter being matched. Determines which keys are valid. Use 'device' for device targeting (pair with key 'device', filter 'equals', and value 'any' | 'mobile' | 'desktop'); 'utm'/'url' for query params; 'urlPath' for the path; and 'country', 'visitor', 'trafficSource', 'referrer', 'cookie', 'landingPage', 'klaviyo', or 'jsExpression' for the rest.",
} as const;

export const AudienceExcludeCurrencyDtoDescriptions = {
  exclude: "Whether currency/geo-based exclusion is active.",
  currency:
    "ISO 4217 currency code to include. Null disables currency-based exclusion.",
  country: "ISO country code to include visitors from.",
  region: "Region or state code to include visitors from.",
  city: "City name to include visitors from.",
} as const;

export const AudienceFilterDtoDescriptions = {
  id: "Server-generated. Read-only.",
  priority:
    "Evaluation order. 0-indexed. Filters with lower values are evaluated first.",
  action:
    "What happens when a visitor matches this filter: 'assignVariation' forces a specific variation (set variationId), 'experienceExclude' removes from the experience, 'randomVariation' assigns randomly.",
  filterType:
    "Category of targeting rule (e.g., UTM parameters, device type, visitor type).",
  expression:
    "Boolean expression tree. Each node has either a 'query' (leaf condition) or 'operator' (logical combinator), not both.",
  expressionType:
    "Whether this uses prebuilt common rules or custom expression logic.",
  variationId:
    "Variation to assign when action is 'assignVariation'. References a variation in the parent experience.",
  excludeFromAnalytics:
    "When true, visitors matching this filter are excluded from analytics even if included in the experience.",
} as const;

export const AudienceDtoDescriptions = {
  id: "Server-generated. Read-only.",
  experienceId: "References the parent experience.",
  enabled:
    "When false, audience rules are ignored and all visitors are eligible.",
  excludeCurrency: "Currency and geo-based visitor exclusion configuration.",
  wholesale:
    "Controls whether wholesale (B2B) customers are included in or excluded from the experience.",
  audienceType:
    "Complexity level of the targeting rules: common (prebuilt), custom (simple), or advanced (full expression trees).",
  elseAction:
    "Fallback for visitors matching no filter: 'randomVariation' assigns randomly across variations (most common), 'assignVariation' forces a specific variation (set elseVariationId), 'experienceExclude' removes them from the experience entirely, 'leaveUnassigned' skips assignment.",
  elseVariationId:
    "Variation to force-assign when elseAction is 'assignVariation'. Typically set to the control variation to default non-targeted visitors to baseline.",
  elseExcludeFromAnalytics:
    "When true, visitors who match no filter are tracked but excluded from analytics calculations.",
  filters:
    "Ordered filter rules evaluated from lowest priority value first (0-indexed). First matching filter determines visitor treatment.",
  evaluationFrequency:
    "How often (in seconds) to re-evaluate audience rules for a returning visitor. Controls targeting freshness.",
} as const;

export const CreateAudienceDtoDescriptions = {
  experienceId:
    "Omit when creating as part of a new experience -- auto-assigned from parent.",
  enabled:
    "Set to true to enforce filter rules. When false, all visitors are included.",
  audienceType:
    "Complexity level: prebuilt common rules, simple custom rules, or advanced expression trees.",
  elseAction:
    "Fallback for visitors matching no filter: 'randomVariation' assigns randomly (most common), 'assignVariation' forces a specific variation (set elseVariationIndex), 'experienceExclude' removes from experience, 'leaveUnassigned' skips assignment.",
  elseVariationIndex:
    "0-based index into the variations array. Use instead of elseVariationId since IDs don't exist yet during creation.",
  elseExcludeFromAnalytics:
    "Set to true to exclude non-matching visitors from analytics.",
  filters:
    "Evaluated in priority order (0-indexed). Each filter defines a targeting condition and resulting action.",
  evaluationFrequency:
    "Seconds between re-evaluations for a visitor. Omit for default.",
} as const;

export const ExternalAudienceDescriptions = {
  id: "Copy from GET response. Identifies which audience config to update.",
} as const;
